import { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { faqItems } from "@/mocks/home";

gsap.registerPlugin(ScrollTrigger);

export default function NewsCarouselSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useEffect(() => {
    const section = sectionRef.current;
    const header = headerRef.current;
    const items = itemsRef.current;
    if (!section || !header || !items) return;

    const faqRows = items.querySelectorAll("[data-faq]");

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top 80%",
        toggleActions: "play none none none",
      },
    });

    tl.fromTo(
      header,
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" }
    );

    tl.fromTo(
      faqRows,
      { y: 40, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.5, stagger: 0.08, ease: "power3.out" },
      "-=0.3"
    );

    return () => {
      tl.scrollTrigger?.kill();
      tl.kill();
    };
  }, []);

  return (
    <section ref={sectionRef} className="bg-background-50 py-20 md:py-28 lg:py-36 px-6 md:px-10 lg:px-14">
      <div className="max-w-3xl mx-auto">
        <div ref={headerRef} className="mb-12">
          <h2 className="font-heading font-bold uppercase text-[26px] sm:text-[36px] md:text-[52px] lg:text-[72px] text-foreground-950 tracking-tight leading-[0.95]">
            Häufige Fragen
          </h2>
        </div>

        <div ref={itemsRef} className="space-y-3">
          {faqItems.map((item, index) => (
            <div
              key={index}
              data-faq
              className="bg-background-100 rounded-2xl overflow-hidden transition-colors duration-200"
            >
              <button
                onClick={() => toggleFaq(index)}
                className="w-full flex items-center justify-between p-5 md:p-6 text-left cursor-pointer group"
              >
                <span className="font-heading font-semibold text-foreground-950 text-sm md:text-base pr-4">
                  {item.question}
                </span>
                <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${openIndex === index ? "bg-accent-500 text-white" : "bg-background-200 text-foreground-500 group-hover:bg-background-300"}`}>
                  <i className={`${openIndex === index ? "ri-subtract-line" : "ri-add-line"} w-4 h-4 flex items-center justify-center text-sm`}></i>
                </span>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  openIndex === index ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <div className="px-5 pb-5 md:px-6 md:pb-6">
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}